import { AppShell, Badge, Box, Button, Group, Text, UnstyledButton } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconCar,
  IconCarGarage,
  IconChartLine,
  IconEye,
  IconFiles,
  IconFolder,
  IconHome,
  IconMail,
  IconTool,
  IconCalendarEvent,
  IconUsers,
} from '@tabler/icons-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { seja, type Instanca, type Uporabnik } from '../seja/seja';
import { useSeja } from '../seja/useSeja';
import { barve } from '../tema';
import { Logotip } from './Logotip';
import { UporabniskiMeni } from './UporabniskiMeni';
import { useZaklep } from '../narocnina/zaklep';
import { novih, usePushVOkviru, useSteviloPovprasevanj } from '../povprasevanja/povprasevanja';
import { AsistentPredal } from '../asistent/AsistentPredal';
import { useDelavnica, useProdaja } from '../seja/paket';
import { sme, usePravice, type Podrocje } from '../seja/pravice';

const VISINA_TRAKU = 40;

/**
 * Glava z logotipom, glavnimi stranmi (izbrana ima rumen poudarek) in uporabniškim menijem. Med
 * prijavo skrbnika kot uporabnik je pod njo rumen trak z gumbom za konec.
 */
export function Okvir() {
  const stanje = useSeja();
  const prijavljen = stanje.status === 'prijavljen' ? stanje : null;
  const prijavaKot = !!prijavljen?.prijavaKot;
  useKonecPrijaveKot(prijavljen);
  usePushVOkviru();
  const novaPovprasevanja = Number(useSteviloPovprasevanj().data?.nova ?? 0);
  const prodaja = useProdaja();
  const delavnica = useDelavnica();
  const pravice = usePravice();
  // strani, za katere uporabnik nima pravice (vloga), niso v meniju
  const vidi = (p: Podrocje) => sme(pravice, p);
  // z obema moduloma je v meniju več povezav: ikone šele na širokem zaslonu, da se glava ne razlomi
  const stevilne = prodaja && delavnica;
  return (
    <AppShell header={{ height: 60 + (prijavaKot ? VISINA_TRAKU : 0) }} padding="md">
      <AppShell.Header style={{ background: barve.ozadjeTemno, borderBottom: 'none' }}>
        <Group h={60} px="md" justify="space-between" wrap="nowrap">
          <Group gap="xl" wrap="nowrap">
            <UnstyledButton component={NavLink} to="/" aria-label="Začetni zaslon">
              <Logotip velikost={24} />
            </UnstyledButton>
            {/* na telefonu in tablici samo logotip (vodi na začetni zaslon), da se glava ne razlomi */}
            <Group gap={4} component="nav" aria-label="Glavni meni" visibleFrom="md" wrap="nowrap">
              <Povezava brezIkone={stevilne} na="/" ikona={<IconHome size={18} />}>
                Začetna
              </Povezava>
              {/* z obema moduloma za vse ni prostora: termini so tedaj na seznamu nalogov in v hitrih dejanjih */}
              {delavnica && !prodaja && vidi('DELAVNICA') && (
                <Povezava brezIkone={stevilne} na="/delavnica/termini" ikona={<IconCalendarEvent size={18} />}>
                  Termini
                </Povezava>
              )}
              {delavnica && vidi('DELAVNICA') && (
                <Povezava brezIkone={stevilne} na="/delavnica/nalogi" ikona={<IconTool size={18} />}>
                  Nalogi
                </Povezava>
              )}
              {/* z obema moduloma za vse ni prostora: vozila strank so tedaj na seznamu nalogov in v hitrih dejanjih */}
              {delavnica && !prodaja && vidi('DELAVNICA') && (
                <Povezava brezIkone={stevilne} na="/delavnica/vozila" ikona={<IconCarGarage size={18} />}>
                  Vozila strank
                </Povezava>
              )}
              {vidi('RACUNI') && (
                <Povezava brezIkone={stevilne} na="/racuni" ikona={<IconFolder size={18} />}>
                  Računi
                </Povezava>
              )}
              {vidi('PREDRACUNI') && (
                <Povezava brezIkone={stevilne} na="/predracuni" ikona={<IconFiles size={18} />}>
                  Predračuni
                </Povezava>
              )}
              {prodaja && vidi('VOZILA') && (
                <Povezava brezIkone={stevilne} na="/vozila" ikona={<IconCar size={18} />}>
                  Vozila
                </Povezava>
              )}
              {vidi('STRANKE') && (
                <Povezava brezIkone={stevilne} na="/osebe" ikona={<IconUsers size={18} />}>
                  Osebe
                </Povezava>
              )}
              {prodaja && vidi('ANALIZE') && (
                <Povezava brezIkone={stevilne} na="/analize" ikona={<IconChartLine size={18} />}>
                  Analize
                </Povezava>
              )}
              {prodaja && vidi('PONUDBA') && (
                <Povezava brezIkone={stevilne} na="/povprasevanja" ikona={<IconMail size={18} />}>
                  Povpraševanja
                  {novaPovprasevanja > 0 && (
                    <Badge size="sm" circle color="red" aria-label={novih(novaPovprasevanja)}>
                      {novaPovprasevanja}
                    </Badge>
                  )}
                </Povezava>
              )}
            </Group>
          </Group>
          {prijavljen && (
            <Group gap="xs" wrap="nowrap">
              {novaPovprasevanja > 0 && (
                // na telefonu in tablici ni menija strani: nova povpraševanja so vidna ob uporabniku
                <Button
                  component={NavLink}
                  to="/povprasevanja"
                  hiddenFrom="md"
                  size="compact-md"
                  color="red"
                  leftSection={<IconMail size={16} />}
                  aria-label={`Povpraševanja: ${novih(novaPovprasevanja)}`}
                >
                  {novaPovprasevanja}
                </Button>
              )}
              {prijavljen.instanca.umetnaInteligenca && <AsistentPredal />}
              <UporabniskiMeni
                uporabnik={prijavljen.uporabnik}
                instanca={prijavljen.instanca}
                skrbnik={prijavljen.skrbnik}
                prijavaKot={prijavljen.prijavaKot}
              />
            </Group>
          )}
        </Group>
        {prijavljen?.prijavaKot && <TrakPrijaveKot uporabnik={prijavljen.uporabnik} instanca={prijavljen.instanca} />}
      </AppShell.Header>
      <AppShell.Main style={{ background: barve.ozadjeStrani }}>
        <VsebinaZaklepa />
      </AppShell.Main>
    </AppShell>
  );
}

/** Zaslon ali, v zaklepu naročnine (403 NAROCNINA_POTEKLA s strežnika), preusmeritev na Naročnino. */
function VsebinaZaklepa() {
  const zaklenjeno = useZaklep();
  const { pathname } = useLocation();
  if (zaklenjeno && pathname !== '/narocnina') {
    return <Navigate to="/narocnina" replace />;
  }
  return <Outlet />;
}

function ime(u: Uporabnik): string {
  return [u.ime, u.priimek].filter(Boolean).join(' ') || u.uporabniskoIme || '';
}

/** Rumen trak med prijavo kot uporabnik: kdo in kje, gumb »Končaj« vrne skrbnikovo sejo. */
function TrakPrijaveKot({ uporabnik, instanca }: { uporabnik: Uporabnik; instanca: Instanca }) {
  const [dela, setDela] = useState(false);
  return (
    <Group
      h={VISINA_TRAKU}
      px="md"
      gap="sm"
      justify="space-between"
      wrap="nowrap"
      role="status"
      style={{ background: barve.poudarek, color: barve.besedilo }}
    >
      <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
        <IconEye size={18} style={{ flexShrink: 0 }} />
        <Text size="sm" truncate>
          Prijavljeni ste kot <b>{ime(uporabnik)}</b>
          <Box component="span" visibleFrom="sm">
            {' '}
            ({uporabnik.uporabniskoIme}) · {instanca.naziv} · prijava se ne beleži
          </Box>
        </Text>
      </Group>
      <Button
        size="compact-sm"
        color="dark"
        loading={dela}
        onClick={() => {
          setDela(true);
          void seja.koncajPrijavoKot().finally(() => setDela(false));
        }}
      >
        Končaj
      </Button>
    </Group>
  );
}

/**
 * Ko se prijava kot uporabnik konča (gumb, potek ali odjava drugje), smo spet skrbnik: nazaj v skrbništvo te instance.
 */
function useKonecPrijaveKot(prijavljen: { prijavaKot: boolean; uporabnik: Uporabnik; instanca: Instanca } | null) {
  const navigate = useNavigate();
  const prejsnja = useRef<{ uporabnik: Uporabnik; instanca: Instanca } | null>(null);
  useEffect(() => {
    if (prijavljen?.prijavaKot) {
      prejsnja.current = { uporabnik: prijavljen.uporabnik, instanca: prijavljen.instanca };
    } else if (prejsnja.current && prijavljen) {
      const { uporabnik, instanca } = prejsnja.current;
      prejsnja.current = null;
      notifications.show({ message: `Prijava kot ${ime(uporabnik)} je končana.` });
      void navigate(`/skrbnistvo/${instanca.id ?? ''}`);
    }
  }, [prijavljen, navigate]);
}

/** Povezava glavnega menija; {@code brezIkone} (z obema moduloma je povezav več) skrije ikono pod širino xl. */
function Povezava({
  na,
  ikona,
  brezIkone = false,
  children,
}: {
  na: string;
  ikona: ReactNode;
  brezIkone?: boolean;
  children: ReactNode;
}) {
  return (
    <UnstyledButton
      component={NavLink}
      to={na}
      end={na === '/'}
      px="sm"
      py={6}
      style={({ isActive }: { isActive: boolean }) => ({
        color: 'white',
        fontWeight: 600,
        fontSize: 14,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        borderBottom: `3px solid ${isActive ? barve.poudarek : 'transparent'}`,
      })}
    >
      {brezIkone ? (
        <Box component="span" visibleFrom="xl" style={{ display: 'flex' }}>
          {ikona}
        </Box>
      ) : (
        ikona
      )}
      {children}
    </UnstyledButton>
  );
}
